"use client";

import {
  ReactNode,
  useEffect,
  useState,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSimpleAuth } from "./SimpleAuthProvider";

// ============================================================================
// USER DATA PROVIDER - FOLLOWING REACT BEST PRACTICES
// ============================================================================
//
// PURPOSE: Handle app user data loading independently of auth
// PRINCIPLES:
// - ✅ Separated concern: Only user data, not auth
// - ✅ Lazy loading: Loads in background after auth
// - ✅ Non-blocking: Doesn't block auth completion
// - ✅ Cached: Avoids duplicate requests
// - ✅ Error resilient: Auth works even if user data fails
//
// CONSUMER APPS: Gate first paint on isInitialized (from SimpleAuthProvider).
// Use this provider's isLoading only where appUser/roles are required.
//
// ============================================================================

type AppUser = {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  roles?: {
    name: string;
    display_name?: string;
    permissions?: any;
  };
  /**
   * Granular per-user grants for sensitive features (keys from the app's
   * SENSITIVE_FEATURES registry, e.g. "plaid"). Selected via `select("*")`;
   * default-deny (empty array). Managed manually in Supabase.
   */
  sensitive_feature_access?: string[] | null;
};

interface UserDataContextType {
  appUser: AppUser | null;
  isLoading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  // Role helpers
  isEmployee: boolean;
  isAdmin: boolean;
  isClientUser: boolean;
  userRole: string | null;
}

const UserDataContext = createContext<UserDataContextType | undefined>(
  undefined
);

interface UserDataProviderProps {
  children: ReactNode;
  supabaseClient: any;
}

export function UserDataProvider({
  children,
  supabaseClient,
}: UserDataProviderProps) {
  const { user, isAuthenticated } = useSimpleAuth();
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prevent duplicate requests
  const pendingRequest = useRef<Promise<AppUser | null> | null>(null);

  // ============================================================================
  // USER DATA FETCHING (Non-blocking)
  // ============================================================================

  const fetchAppUser = useCallback(
    async (userId: string): Promise<AppUser | null> => {
      // Check for existing pending request (deduplication)
      if (pendingRequest.current) {
        if (process.env.NODE_ENV === "development") {
          console.log("🔍 UserData: Deduplicating request for userId:", userId);
        }
        return pendingRequest.current;
      }

      if (process.env.NODE_ENV === "development") {
        console.log("🔍 UserData: Fetching app user for userId:", userId);
      }

      // Create new request promise
      const requestPromise = (async () => {
        try {
          const { data, error } = await supabaseClient
            .from("app_users")
            .select(`
              *,
              roles:role_id (
                name,
                display_name,
                permissions
              )
            `)
            .eq("id", userId)
            .single();

          if (error) {
            console.error("Error fetching app user:", error);
            throw new Error(error.message);
          }

          return data as AppUser;
        } catch (error) {
          console.error("Error fetching app user:", error);
          throw error;
        } finally {
          // Clean up pending request
          pendingRequest.current = null;
        }
      })();

      // Store pending request for deduplication
      pendingRequest.current = requestPromise;
      return requestPromise;
    },
    [supabaseClient]
  );

  const refreshUser = useCallback(async () => {
    if (!user) return;

    setIsLoading(true);
    setError(null);

    try {
      const userData = await fetchAppUser(user.id);
      setAppUser(userData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      setError(errorMessage);
      console.error("Failed to refresh user data:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user, fetchAppUser]);

  // ============================================================================
  // EFFECT: LOAD USER DATA WHEN AUTHENTICATED
  // ============================================================================

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setAppUser(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    // Load user data in background (non-blocking)
    // Don't include refreshUser in deps to avoid infinite loop
    refreshUser();
  }, [isAuthenticated, user?.id]);

  // ============================================================================
  // COMPUTED VALUES (Memoized for Performance)
  // ============================================================================

  const userRole = useMemo(
    () => appUser?.roles?.name || appUser?.role || null,
    [appUser]
  );

  const isEmployee = useMemo(
    () => userRole === "wtc_employee" || userRole === "wtc_admin" || userRole === "wtc_crm",
    [userRole]
  );

  const isAdmin = useMemo(() => userRole === "wtc_admin", [userRole]);

  const isClientUser = useMemo(() => userRole === "client_user", [userRole]);

  const contextValue: UserDataContextType = {
    appUser,
    isLoading,
    error,
    refreshUser,
    isEmployee,
    isAdmin,
    isClientUser,
    userRole,
  };

  return (
    <UserDataContext.Provider value={contextValue}>
      {children}
    </UserDataContext.Provider>
  );
}

// ============================================================================
// HOOK FOR CONSUMING USER DATA
// ============================================================================

export function useUserData(): UserDataContextType {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error("useUserData must be used within a UserDataProvider");
  }
  return context;
}

// Non-throwing variant for boundary components (not-found / error pages) that
// may render OUTSIDE the provider tree. Returns null when no provider is mounted.
export function useUserDataOptional(): UserDataContextType | null {
  return useContext(UserDataContext) ?? null;
}

// ============================================================================
// CONVENIENCE HOOKS
// ============================================================================

export function useAppUser() {
  const { appUser } = useUserData();
  return appUser;
}

export function useUserRole() {
  const { userRole } = useUserData();
  return userRole;
}

export function useIsEmployee() {
  const { isEmployee } = useUserData();
  return isEmployee;
}

export function useIsAdmin() {
  const { isAdmin } = useUserData();
  return isAdmin;
}

export function useIsClientUser() {
  const { isClientUser } = useUserData();
  return isClientUser;
}
